import Popup from "reactjs-popup";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { calcShippingCosts } from "../Checkout/Shipping";
import { useGlobalState } from "../GlobalContext";
import { Accordion } from "../Accordion/Accordion";
import RetrieveProductSettings from "../ProductSettings/RetrieveProductSettings";
import { getCurrentLabel } from "../Functions/functions";
import { getModelPrice } from "../Functions/price-calculations";
import { fetchOrderPriceBreakdown } from "../Functions/pricingApi";
import { apiLabelUrl, isEU } from "../Functions/functions";
import useProductSettingsExclusions from "../Hooks/useProductSettingsExclusions";

export default function OrderPopup(props) {
    const [editModalOpen, setEditModalOpen] = useState(null);
    const [textData, setTextData] = useState(null);
    const [showOnlyDownloadBtn, setShowOnlyDownloadBtn] = useState(false);
    const [shippingCosts, setShippingCosts] = useState(0);
    const { productSettings, loggedInUser, labelLogo, impersonating, setImpersonating } = useGlobalState();
    const [totalPrice, setTotalPrice] = useState(0);
    const [disableCheckoutButton, setDisableCheckoutButton] = useState(true);
    const [mandatoryProductSettings, setMandatoryProductSettings] = useState([]);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [currentLabel, setCurrentLabel] = useState(null);
    const [priceBreakdown, setPriceBreakdown] = useState(null);
    const [productSettingsExclusions, setProductSettingsExclusions] = useProductSettingsExclusions(
        props.loggedInUser ?? loggedInUser
    );
    const [documents, setDocuments] = useState([]);

    // When viewing an existing order, prefer the order's saved exclusions snapshot
    const appliedProductSettingsExclusions = props.order?.product_settings_exclusions ?? productSettingsExclusions;

    // Check impersonating status only once on mount
    useEffect(() => {
        const impersonatingDiv = document.getElementById("impersonate");
        const isImpersonating = impersonatingDiv !== null && impersonatingDiv !== undefined;
        // Only update if the value is different to avoid triggering re-renders
        if (isImpersonating !== impersonating) {
            setImpersonating(isImpersonating);
        }

        getCurrentLabel().then((label) => {
            setCurrentLabel(label);
        });
    }, [impersonating, setImpersonating]);

    // Handle textData and product settings when props change
    useEffect(() => {
        RetrieveProductSettings({ setMandatoryProductSettings });
        setTextData(props.textData);
    }, [props.textData]);

    // Handle showOnlyDownloadBtn based on impersonating state
    useEffect(() => {
        if (props.type === "order" && !impersonating) {
            setShowOnlyDownloadBtn(true);
        }
    }, [props.type, impersonating]);

    useEffect(() => {
        if (!props.order?.project_number) return;

        const extraCosts = Array.isArray(props.order.extraCosts) ? props.order.extraCosts : [];
        const companyDiscountFromExtraCosts = extraCosts.reduce((total, cost) => {
            const name = (cost?.name ?? "").toLowerCase();
            if (!name.includes("company discount")) {
                return total;
            }

            const parsedPrice = parseFloat(cost?.price ?? 0);
            if (!Number.isFinite(parsedPrice)) {
                return total;
            }

            return total + Math.abs(parsedPrice);
        }, 0);

        const discountCodesFromExtraCosts = extraCosts.reduce((total, cost) => {
            const name = (cost?.name ?? "").toLowerCase();
            if (!name.includes("discount code")) {
                return total;
            }

            const parsedPrice = parseFloat(cost?.price ?? 0);
            if (!Number.isFinite(parsedPrice)) {
                return total;
            }

            return total + Math.abs(parsedPrice);
        }, 0);

        const mandatoryCosts = extraCosts.filter((cost) => {
            const name = (cost?.name ?? "").toLowerCase();
            return !name.includes("company discount") && !name.includes("discount code");
        });

        let companyDiscount = Math.abs(companyDiscountFromExtraCosts);
        if (companyDiscount === 0) {
            const orderDiscountValue = parseFloat(props.order.discount ?? 0);
            const orderSubtotal = parseFloat(props.order.subtotal ?? 0);
            if (orderDiscountValue > 0 && orderSubtotal > 0) {
                if (props.order.fixed_discount) {
                    companyDiscount = Math.abs(orderDiscountValue);
                } else {
                    companyDiscount = Math.round(((orderSubtotal * orderDiscountValue) / 100) * 100) / 100;
                }
            }
        }

        // Use snapshot prices from order object instead of fetching live pricing
        const breakdown = {
            subtotal: parseFloat(props.order.subtotal ?? 0),
            vat: parseFloat(props.order.vat_total ?? 0),
            shipping: parseFloat(props.order.delivery_price ?? 0),
            total: parseFloat(props.order.total_price ?? 0),
            company_discount: companyDiscount,
            discount_codes_amount: Math.abs(discountCodesFromExtraCosts),
            mandatory_costs: mandatoryCosts,
            pricing_version: Number(props.order?.pricing_version ?? 0),
            metadata: {
                pricing_version: Number(props.order?.pricing_version ?? 0),
            },
            lines: {
                models: [],
                library_products: [],
                mandatory_costs: mandatoryCosts,
            },
            is_snapshot: true,
        };

        setPriceBreakdown(breakdown);
        setTotalPrice(breakdown.total);
        setTotalDiscount(breakdown.company_discount + breakdown.discount_codes_amount);
        disableAgreeButton();
    }, [props.order?.project_number]);

    useEffect(() => {
        if (!currentLabel || !props.order?.project_number) return;

        fetchOrderPriceBreakdown(currentLabel, props.order.project_number)
            .then((data) => {
                const totals = data?.totals ?? {};
                const lines = data?.lines ?? {};
                const metadata = data?.metadata ?? {};

                setPriceBreakdown((prev) => ({
                    ...prev,
                    subtotal: data?.subtotal ?? totals?.subtotal ?? prev?.subtotal ?? 0,
                    vat: data?.vat ?? totals?.vat ?? prev?.vat ?? 0,
                    total: data?.total ?? totals?.total ?? prev?.total ?? 0,
                    shipping: data?.shipping ?? totals?.shipping ?? prev?.shipping ?? 0,
                    company_discount: data?.company_discount ?? totals?.company_discount ?? prev?.company_discount ?? 0,
                    discount_codes_amount:
                        data?.discount_codes_amount ??
                        totals?.discount_codes_amount ??
                        prev?.discount_codes_amount ??
                        0,
                    minimum_order_surcharge:
                        data?.minimum_order_surcharge ??
                        totals?.minimum_order_surcharge ??
                        prev?.minimum_order_surcharge ??
                        0,
                    mandatory_costs: data?.mandatory_costs ?? lines?.mandatory_costs ?? prev?.mandatory_costs ?? [],
                    lines: {
                        models: lines?.models ?? prev?.lines?.models ?? [],
                        library_products: lines?.library_products ?? prev?.lines?.library_products ?? [],
                        mandatory_costs:
                            lines?.mandatory_costs ?? data?.mandatory_costs ?? prev?.lines?.mandatory_costs ?? [],
                    },
                    pricing_version:
                        Number(data?.pricing_version ?? metadata?.pricing_version ?? prev?.pricing_version ?? 0) || 0,
                    metadata: {
                        ...(prev?.metadata ?? {}),
                        ...metadata,
                        pricing_version: Number(metadata?.pricing_version ?? prev?.metadata?.pricing_version ?? 0) || 0,
                    },
                }));
                setTotalPrice(data?.total ?? totals?.total ?? 0);
            })
            .catch(() => {
                // Keep existing breakdown on error
            });
    }, [currentLabel, props.order?.project_number]);

    useEffect(() => {
        let country = null;
        if (loggedInUser.company !== null) {
            country = loggedInUser.company.country;
        }

        if (typeof props.order.delivery_price !== "undefined" && props.order.delivery_price !== null) {
            setShippingCosts(parseFloat(props.order.delivery_price));
        } else {
            if (props.order.delivery_term !== null) {
                calcShippingCosts([{ data: props.order.models }], productSettings, country).then((result) => {
                    if (typeof result.shipping_costs !== "undefined" && result.shipping_costs !== null) {
                        setShippingCosts(result.shipping_costs.road_goods_euro_price);
                    } else {
                        setShippingCosts((0).toFixed(2));
                    }
                });
            } else {
                setShippingCosts((0).toFixed(2));
            }
        }
    }, []);

    useEffect(() => {
        if (props.order?.project_number) {
            apiLabelUrl("api").then((baseUrl) => {
                const userId = loggedInUser?.user?.id;
                fetch(`${baseUrl}/orders/${props.order.project_number}/documents?user_id=${userId}`, {
                    headers: { Accept: "application/json" },
                })
                    .then((res) => res.json())
                    .then((data) => setDocuments(data.documents || []))
                    .catch(() => setDocuments([]));
            });
        }
    }, [props.order?.project_number]);

    const handleDownload = async () => {
        // make download link
        const downloadLink = document.createElement("a");
        downloadLink.href = (await apiLabelUrl("web")) + `/quotation/generate-pdf/` + props.order.project_number;
        var today = new Date().toLocaleDateString("nl-NL");
        today = today.replace(/-/g, "");
        downloadLink.setAttribute("download", `YO${props.order.project_number}${today}.pdf`);
        document.body.appendChild(downloadLink);
        setTimeout(() => {
            downloadLink.click();
        }, 100);
    };

    const showOrderBtw = (order) => {
        let country = null;
        if (order?.delivery_country) {
            country = order?.delivery_country;
        }

        if (
            country !== null &&
            isEU(country) &&
            loggedInUser?.company?.vat_number !== null &&
            typeof loggedInUser?.company?.vat_number !== "undefined" &&
            country !== "Netherlands"
        ) {
            return false;
        }
        return true;
    };

    const handleEdit = () => {
        // TODO: Refer to edit page
        props.navigateToPageName("account_edit_quotation", "?id=" + props.order.project_number);
    };

    const handleAgree = () => {
        props.navigateToPageName("checkout", "?id=" + props.order.project_number);
    };

    // Checkout button
    const disableAgreeButton = () => {
        let found = false;
        for (let model in props.order.models) {
            // Check if numeric or not
            if (isNaN(parseFloat(props.order.models[model].pivot.price))) {
                found = true;
            }
        }

        if (found) {
            setDisableCheckoutButton(true);
        } else {
            setDisableCheckoutButton(false);
        }
    };

    const addMandatoryOption = (mdl) => {
        let result = false;
        props.order.models.forEach((model) => {
            if (mdl.id === model.id) {
                mandatoryProductSettings.forEach((setting) => {
                    if (model?.settings?.application === setting?.application) {
                        if (model?.settings?.application === "Medical" && model?.settings?.sterile === false) {
                            // return the clinical cost of 0
                            result = {
                                price: "0.00",
                                name: textData["sections/label"]["mandatory"]["clinical"],
                            };
                            return result;
                        }

                        // if dental and short term, return 0
                        if (
                            model?.settings?.application === "Dental" &&
                            model?.settings?.intendedUse.includes("Short term use")
                        ) {
                            result = {
                                price: "0.00",
                                name: textData["sections/label"]["mandatory"]["short_term"],
                            };
                            return result;
                        }

                        result = {
                            price: setting.mandatory_options[0].price.toFixed(2),
                            name: setting.mandatory_options[0].name,
                        };
                    }
                });
            }
        });
        return result;
    };

    const getMandatoryOptionLines = () => {
        const seen = new Set();

        return (props.order.models ?? [])
            .map((model) => addMandatoryOption(model))
            .filter((option) => {
                if (!option || seen.has(option.name)) return false;
                seen.add(option.name);
                return true;
            });
    };

    const getDisplayPricing = () => {
        const pricingVersion = Number(props.order?.pricing_version ?? priceBreakdown?.metadata?.pricing_version ?? 0);
        const isLegacyPricingV1 = pricingVersion === 1;

        const backendMandatoryTotal = (priceBreakdown?.mandatory_costs ?? []).reduce(
            (sum, cost) => sum + (parseFloat(cost?.price ?? 0) || 0),
            0
        );

        const fallbackMandatoryTotal =
            !isLegacyPricingV1 || backendMandatoryTotal > 0
                ? 0
                : getMandatoryOptionLines().reduce((sum, option) => sum + (parseFloat(option?.price ?? 0) || 0), 0);

        const baseSubtotal = parseFloat(priceBreakdown?.subtotal ?? 0) || 0;
        const baseShipping =
            parseFloat(priceBreakdown?.shipping ?? props.order.delivery_price ?? shippingCosts ?? 0) || 0;
        const baseVat = parseFloat(priceBreakdown?.vat ?? 0) || 0;
        const baseTotal = parseFloat(priceBreakdown?.total ?? 0) || 0;

        const modelLinesSubtotal = (priceBreakdown?.lines?.models ?? []).reduce(
            (sum, line) => sum + (parseFloat(line?.final_total ?? 0) || 0),
            0
        );
        const libraryLinesSubtotal = (priceBreakdown?.lines?.library_products ?? []).reduce(
            (sum, line) => sum + (parseFloat(line?.final_total ?? 0) || 0),
            0
        );
        const linesSubtotal = modelLinesSubtotal + libraryLinesSubtotal;

        const shouldApplyFallbackMandatory =
            fallbackMandatoryTotal > 0 && Math.abs(baseSubtotal - linesSubtotal) < 0.02;
        const mandatoryDelta = shouldApplyFallbackMandatory ? fallbackMandatoryTotal : 0;

        const minimumOrderSurcharge = parseFloat(priceBreakdown?.minimum_order_surcharge ?? 0) || 0;

        const taxableBase = baseSubtotal + baseShipping;
        const inferredVatRate = taxableBase > 0 ? baseVat / taxableBase : 0;
        const extraVat = showOrderBtw(props.order)
            ? Math.max(0, Math.round(mandatoryDelta * inferredVatRate * 100) / 100)
            : 0;

        return {
            subtotal: baseSubtotal + mandatoryDelta + minimumOrderSurcharge,
            vat: baseVat + extraVat,
            total: baseTotal + mandatoryDelta + extraVat,
        };
    };

    const OrderOverviewAccordion = (model, index) => {
        return (
            <>
                <img src={model.preview_image} className={"logo"} alt="exampleModel" />
                <div className="product-container">
                    <div>
                        <p className="title">
                            {model.product_name || model.file} <span className="amount">{model.pivot.amount}x</span>
                        </p>
                        {model.library_labels && model.library_labels.length > 0 && (
                            <div className="cart-product-labels">
                                {model.library_labels.map((label, i) => (
                                    <span
                                        key={i}
                                        className="cart-product-label-chip"
                                        style={{ backgroundColor: label.color || "#6b7280" }}
                                    >
                                        {label.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="product-data">
                        {model.settings.application !== null && typeof model.settings.application !== "undefined" && (
                            <span className="product-setting">{model.settings.application}</span>
                        )}
                        {model.settings.sterile !== null &&
                            model.settings.sterile === true &&
                            model.settings.application.toLowerCase() === "medical" &&
                            typeof model.settings.sterile !== "undefined" && (
                                <span className="product-setting">
                                    {textData["sections/label"]["sterile"]["sterile"]}
                                </span>
                            )}
                        {model.settings.intendedUse !== null &&
                            (model.settings.application.toLowerCase() === "medical" ||
                                model.settings.application.toLowerCase() === "dental") &&
                            typeof model.settings.intendedUse !== "undefined" && (
                                <span className="product-setting">{model.settings.intendedUse}</span>
                            )}
                        {model.settings.clinicalUse !== null &&
                            model.settings.application.toLowerCase() === "medical" &&
                            typeof model.settings.clinicalUse !== "undefined" && (
                                <span className="product-setting">
                                    {model.settings.sterile === true
                                        ? textData["sections/label"]["clinicalUse"]["clinical"]
                                        : textData["sections/label"]["clinicalUse"]["not_clinical"]}
                                </span>
                            )}
                        {model.settings.custom !== null &&
                            model.settings.custom === true &&
                            model.settings.application.toLowerCase() === "medical" &&
                            typeof model.settings.custom !== "undefined" && (
                                <span className="product-setting">{textData["sections/custom"]["custom"]}</span>
                            )}
                        {model.settings.case !== null &&
                            model.settings.application.toLowerCase() === "medical" &&
                            typeof model.settings.case !== "undefined" && (
                                <span className="product-setting">
                                    {textData["sections/custom"]["case"]}
                                    {": "}
                                    {model.settings.case}
                                </span>
                            )}
                        {model.settings.practitioner !== null &&
                            model.settings.application.toLowerCase() === "medical" &&
                            typeof model.settings.practitioner !== "undefined" && (
                                <span className="product-setting">
                                    {textData["sections/custom"]["practitioner"]}
                                    {": "}
                                    {model.settings.practitioner}
                                </span>
                            )}
                        {model.settings.material !== null && (
                            <span className="product-setting">{model.settings.material}</span>
                        )}
                        {model.settings.color !== null && model.settings.color.toLowerCase() !== "no colouring" && (
                            <span className="product-setting">{model.settings.color}</span>
                        )}
                        {model.settings.polish !== null && model.settings.polish.toLowerCase() !== "no" && (
                            <span className="product-setting">{model.settings.polish}</span>
                        )}
                        {model.settings.coating !== null && model.settings.coating.toLowerCase() !== "no" && (
                            <span className="product-setting">{model.settings.coating}</span>
                        )}
                        {model.settings.orientation !== null && (
                            <span className="product-setting">{model.settings.orientation}</span>
                        )}
                    </div>
                </div>
                <div className="item-price-section">
                    <p id="last-p">{getModelPrice(model, loggedInUser, props.order)}</p>
                    {model.item_extra_costs && model.item_extra_costs.length > 0 && (
                        <div className="item-extra-costs">
                            {model.item_extra_costs.map((cost, i) => (
                                <div className="item-extra-cost-row" key={i}>
                                    <span className="item-extra-cost-name">
                                        {cost.name}
                                        {cost.description ? ` — ${cost.description}` : ""}
                                    </span>
                                    <span className="item-extra-cost-price">
                                        + &euro;{parseFloat(cost.calculated_price).toFixed(2).replace(".", ",")}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </>
        );
    };

    const OrderOverviewModal = () => (
        <>
            {textData && (
                <Popup open={true} modal nested className="order-overview-popup">
                    {(close) => (
                        <div className="modal">
                            <button className="close" onClick={close}>
                                &times;
                            </button>
                            <div className="header header-small">
                                <Link to={"/"}>
                                    <img src={labelLogo?.url} className={"logo"} alt="logo" />
                                </Link>
                                <div className="title-section">
                                    <p>
                                        {props.type === "savedQuotation"
                                            ? textData["popups/order_overview"]["quotation"]
                                            : textData["popups/order_overview"]["order"]}{" "}
                                        #{props.order.project_number}
                                    </p>
                                    <span className={props.order.orderStatus}>{props.order.orderStatus}</span>
                                </div>
                                <div className="tags">
                                    {props.order.reference !== null && <p>{props.order.reference}</p>}
                                    <p>{props.order.orderDate}</p>
                                </div>
                            </div>
                            <div className="content scrollable">
                                {typeof props.order.reject_reason !== "undefined" &&
                                    props.order.reject_reason !== null && (
                                        <>
                                            <div className="reject-reason">
                                                <p>{textData["account/order_overview"]["reject_reason"]}:</p>
                                                <p>
                                                    {props.order.reject_reason.split("\n").map((item, idx) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                {item}
                                                                <br />
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </p>
                                            </div>
                                        </>
                                    )}

                                {(shippingCosts !== "..." || props.order.status !== "quotation") && (
                                    <>
                                        <div className="content-products">
                                            <>
                                                {!props.responsive
                                                    ? props.order.models.map((model, index) => {
                                                          return (
                                                              <div className="product-overview" key={index}>
                                                                  <img
                                                                      src={model.preview_image}
                                                                      className={"logo"}
                                                                      alt="exampleModel"
                                                                  />
                                                                  <div className="product-container">
                                                                      <div>
                                                                          <p className="title">
                                                                              {model.product_name || model.file}{" "}
                                                                              <span className="amount">
                                                                                  {model.pivot.amount}x
                                                                              </span>
                                                                          </p>
                                                                          {model.library_labels &&
                                                                              model.library_labels.length > 0 && (
                                                                                  <div className="cart-product-labels">
                                                                                      {model.library_labels.map(
                                                                                          (label, i) => (
                                                                                              <span
                                                                                                  key={i}
                                                                                                  className="cart-product-label-chip"
                                                                                                  style={{
                                                                                                      backgroundColor:
                                                                                                          label.color ||
                                                                                                          "#6b7280",
                                                                                                  }}
                                                                                              >
                                                                                                  {label.name}
                                                                                              </span>
                                                                                          )
                                                                                      )}
                                                                                  </div>
                                                                              )}
                                                                      </div>
                                                                      <div className="product-data">
                                                                          {model?.settings?.application !== null &&
                                                                              typeof model?.settings?.application !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.application}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.sterile !== null &&
                                                                              model?.settings?.sterile === true &&
                                                                              model?.settings?.application.toLowerCase() ===
                                                                                  "medical" &&
                                                                              typeof model?.settings?.sterile !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {
                                                                                          textData["sections/label"][
                                                                                              "sterile"
                                                                                          ]["sterile"]
                                                                                      }
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.intendedUse !== null &&
                                                                              (model?.settings?.application.toLowerCase() ===
                                                                                  "medical" ||
                                                                                  model?.settings?.application.toLowerCase() ===
                                                                                      "dental") &&
                                                                              typeof model?.settings?.intendedUse !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.intendedUse}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.clinicalUse !== null &&
                                                                              model?.settings?.application.toLowerCase() ===
                                                                                  "medical" &&
                                                                              typeof model?.settings?.clinicalUse !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.sterile === true
                                                                                          ? textData["sections/label"][
                                                                                                "clinicalUse"
                                                                                            ]["clinical"]
                                                                                          : textData["sections/label"][
                                                                                                "clinicalUse"
                                                                                            ]["not_clinical"]}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.custom !== null &&
                                                                              model?.settings?.custom === true &&
                                                                              model?.settings?.application.toLowerCase() ===
                                                                                  "medical" &&
                                                                              typeof model?.settings?.custom !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {
                                                                                          textData["sections/custom"][
                                                                                              "custom"
                                                                                          ]
                                                                                      }
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.case !== null &&
                                                                              model?.settings?.application.toLowerCase() ===
                                                                                  "medical" &&
                                                                              typeof model?.settings?.case !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {
                                                                                          textData["sections/custom"][
                                                                                              "case"
                                                                                          ]
                                                                                      }
                                                                                      {": "}
                                                                                      {model?.settings?.case}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.practitioner !== null &&
                                                                              model?.settings?.application.toLowerCase() ===
                                                                                  "medical" &&
                                                                              typeof model?.settings?.practitioner !==
                                                                                  "undefined" && (
                                                                                  <span className="product-setting">
                                                                                      {
                                                                                          textData["sections/custom"][
                                                                                              "practitioner"
                                                                                          ]
                                                                                      }
                                                                                      {": "}
                                                                                      {model?.settings?.practitioner}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.material !== null && (
                                                                              <span className="product-setting">
                                                                                  {model?.settings?.material}
                                                                              </span>
                                                                          )}
                                                                          {model?.settings?.color !== null &&
                                                                              model?.settings?.color.toLowerCase() !==
                                                                                  "no colouring" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.color}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.polish !== null &&
                                                                              model?.settings?.polish.toLowerCase() !==
                                                                                  "no" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.polish}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.coating !== null &&
                                                                              model?.settings?.coating.toLowerCase() !==
                                                                                  "no" && (
                                                                                  <span className="product-setting">
                                                                                      {model?.settings?.coating}
                                                                                  </span>
                                                                              )}
                                                                          {model?.settings?.orientation !== null && (
                                                                              <span className="product-setting">
                                                                                  {model?.settings?.orientation}
                                                                              </span>
                                                                          )}
                                                                          {/*addMandatoryOption(model) !== false && (
                                                                              <>
                                                                                  <span className="product-setting">
                                                                                      {
                                                                                          textData["sections/label"][
                                                                                              "mandatory"
                                                                                          ]
                                                                                      }
                                                                                      :&nbsp;
                                                                                      {addMandatoryOption(model).name}
                                                                                      &nbsp; &euro;
                                                                                      {addMandatoryOption(model).price}
                                                                                  </span>
                                                                              </>
                                                                          )*/}
                                                                      </div>
                                                                  </div>
                                                                  <div className="item-price-section">
                                                                      <p id="last-p">
                                                                          {getModelPrice(
                                                                              model,
                                                                              loggedInUser,
                                                                              props.order
                                                                          )}
                                                                      </p>
                                                                      {model.item_extra_costs &&
                                                                          model.item_extra_costs.length > 0 && (
                                                                              <div className="item-extra-costs">
                                                                                  {model.item_extra_costs.map(
                                                                                      (cost, i) => (
                                                                                          <div
                                                                                              className="item-extra-cost-row"
                                                                                              key={i}
                                                                                          >
                                                                                              <span className="item-extra-cost-name">
                                                                                                  {cost.name}
                                                                                                  {cost.description
                                                                                                      ? ` — ${cost.description}`
                                                                                                      : ""}
                                                                                              </span>
                                                                                              <span className="item-extra-cost-price">
                                                                                                  + &euro;
                                                                                                  {parseFloat(
                                                                                                      cost.calculated_price
                                                                                                  )
                                                                                                      .toFixed(2)
                                                                                                      .replace(
                                                                                                          ".",
                                                                                                          ","
                                                                                                      )}
                                                                                              </span>
                                                                                          </div>
                                                                                      )
                                                                                  )}
                                                                              </div>
                                                                          )}
                                                                  </div>
                                                              </div>
                                                          );
                                                      })
                                                    : props.order.models.map((model, index) => {
                                                          return (
                                                              <div className="product-overview" key={index}>
                                                                  <Accordion
                                                                      header={model.product_name || model.file}
                                                                      content={OrderOverviewAccordion(model, index)}
                                                                      model={model}
                                                                  ></Accordion>
                                                              </div>
                                                          );
                                                      })}
                                                {typeof props.order.products !== "undefined" && (
                                                    <>
                                                        {props.order.products.map((libraryProduct, index) => {
                                                            return (
                                                                <div className="product-overview" key={index}>
                                                                    <img
                                                                        src={libraryProduct.product.preview_image}
                                                                        className={"logo"}
                                                                        alt="exampleModel"
                                                                        width="100"
                                                                        height="100"
                                                                    />
                                                                    <div className="product-container">
                                                                        <div>
                                                                            <p className="title">
                                                                                {libraryProduct.product.name}{" "}
                                                                                <span className="amount">
                                                                                    {libraryProduct.quantity}x
                                                                                </span>
                                                                            </p>
                                                                        </div>
                                                                        <div className="product-data">
                                                                            {libraryProduct.product.settings
                                                                                .application !== null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .application !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.application
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings.sterile !==
                                                                                null &&
                                                                                libraryProduct.product.settings
                                                                                    .sterile === true &&
                                                                                libraryProduct.product.settings.application.toLowerCase() ===
                                                                                    "medical" &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .sterile !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            textData["sections/label"][
                                                                                                "sterile"
                                                                                            ]["sterile"]
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings
                                                                                .intendedUse !== null &&
                                                                                ((typeof libraryProduct.product.settings
                                                                                    .application === "string" &&
                                                                                    libraryProduct.product.settings.application.toLowerCase() ===
                                                                                        "medical") ||
                                                                                    (typeof libraryProduct.product
                                                                                        .settings.application ===
                                                                                        "string" &&
                                                                                        libraryProduct.product.settings.application.toLowerCase() ===
                                                                                            "dental")) &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .intendedUse !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.intendedUse
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings
                                                                                .clinicalUse !== null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .application === "string" &&
                                                                                libraryProduct.product.settings.application.toLowerCase() ===
                                                                                    "medical" &&
                                                                                libraryProduct.product.settings
                                                                                    .sterile === true &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .clinicalUse !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            textData["sections/label"][
                                                                                                "clinicalUse"
                                                                                            ]["clinical"]
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings.custom !==
                                                                                null &&
                                                                                libraryProduct.product.settings
                                                                                    .custom === true &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .application === "string" &&
                                                                                libraryProduct.product.settings.application.toLowerCase() ===
                                                                                    "medical" &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .custom !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            textData["sections/custom"][
                                                                                                "custom"
                                                                                            ]
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings.case !==
                                                                                null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .application === "string" &&
                                                                                libraryProduct.product.settings.application.toLowerCase() ===
                                                                                    "medical" &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .case !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            textData["sections/custom"][
                                                                                                "case"
                                                                                            ]
                                                                                        }
                                                                                        {": "}
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.case
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings
                                                                                .practitioner !== null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .application === "string" &&
                                                                                libraryProduct.product.settings.application.toLowerCase() ===
                                                                                    "medical" &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .practitioner !== "undefined" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            textData["sections/custom"][
                                                                                                "practitioner"
                                                                                            ]
                                                                                        }
                                                                                        {": "}
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.practitioner
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings
                                                                                .material !== null && (
                                                                                <span className="product-setting">
                                                                                    {
                                                                                        libraryProduct.product.settings
                                                                                            .material
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                            {libraryProduct.product.settings.color !==
                                                                                null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .color === "string" &&
                                                                                libraryProduct.product.settings.color.toLowerCase() !==
                                                                                    "no colouring" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.color
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings.polish !==
                                                                                null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .polish === "string" &&
                                                                                libraryProduct.product.settings.polish.toLowerCase() !==
                                                                                    "no" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.polish
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings.coating !==
                                                                                null &&
                                                                                typeof libraryProduct.product.settings
                                                                                    .coating === "string" &&
                                                                                libraryProduct.product.settings.coating.toLowerCase() !==
                                                                                    "no" && (
                                                                                    <span className="product-setting">
                                                                                        {
                                                                                            libraryProduct.product
                                                                                                .settings.coating
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            {libraryProduct.product.settings
                                                                                .orientation !== null && (
                                                                                <span className="product-setting">
                                                                                    {
                                                                                        libraryProduct.product.settings
                                                                                            .orientation
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                            {/* {addMandatoryOption(libraryProduct.product) !== false && (
                                                                                    <>
                                                                                        <span className="product-setting">
                                                                                            {
                                                                                                textData["sections/label"][
                                                                                                    "mandatory"
                                                                                                ]
                                                                                            }
                                                                                            :&nbsp;
                                                                                            {addMandatoryOption(libraryProduct.product).name}&nbsp;
                                                                                            &euro;
                                                                                            {addMandatoryOption(libraryProduct.product).price}
                                                                                        </span>
                                                                                    </>
                                                                                )} */}
                                                                        </div>
                                                                    </div>
                                                                    <div className="item-price-section">
                                                                        <p id="last-p">
                                                                            {parseFloat(
                                                                                libraryProduct.discount_multiplier ?? 1
                                                                            ) !== 1 ? (
                                                                                <>
                                                                                    <s>
                                                                                        &euro;
                                                                                        {(
                                                                                            parseFloat(
                                                                                                libraryProduct.price
                                                                                            ) *
                                                                                            parseInt(
                                                                                                libraryProduct.quantity
                                                                                            )
                                                                                        ).toFixed(2)}
                                                                                    </s>
                                                                                    <br />
                                                                                    &euro;
                                                                                    {(
                                                                                        parseFloat(
                                                                                            libraryProduct.price
                                                                                        ) *
                                                                                        parseFloat(
                                                                                            libraryProduct.discount_multiplier
                                                                                        ) *
                                                                                        parseInt(
                                                                                            libraryProduct.quantity
                                                                                        )
                                                                                    ).toFixed(2)}
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    &euro;
                                                                                    {(
                                                                                        parseFloat(
                                                                                            libraryProduct.price
                                                                                        ) *
                                                                                        parseInt(
                                                                                            libraryProduct.quantity
                                                                                        )
                                                                                    ).toFixed(2)}
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                        {libraryProduct.item_extra_costs &&
                                                                            libraryProduct.item_extra_costs.length >
                                                                                0 && (
                                                                                <div className="item-extra-costs">
                                                                                    {libraryProduct.item_extra_costs.map(
                                                                                        (cost, i) => (
                                                                                            <div
                                                                                                className="item-extra-cost-row"
                                                                                                key={i}
                                                                                            >
                                                                                                <span className="item-extra-cost-name">
                                                                                                    {cost.name}
                                                                                                    {cost.description
                                                                                                        ? ` — ${cost.description}`
                                                                                                        : ""}
                                                                                                </span>
                                                                                                <span className="item-extra-cost-price">
                                                                                                    + &euro;
                                                                                                    {parseFloat(
                                                                                                        cost.calculated_price
                                                                                                    )
                                                                                                        .toFixed(2)
                                                                                                        .replace(
                                                                                                            ".",
                                                                                                            ","
                                                                                                        )}
                                                                                                </span>
                                                                                            </div>
                                                                                        )
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </>
                                        </div>
                                        <div className="price-overview">
                                            {(() => {
                                                const extraCostLines = priceBreakdown?.mandatory_costs ?? [];
                                                const isLegacyPricingV1 =
                                                    Number(
                                                        props.order?.pricing_version ??
                                                            priceBreakdown?.metadata?.pricing_version ??
                                                            0
                                                    ) === 1;
                                                const mandatoryOptionLines = isLegacyPricingV1
                                                    ? getMandatoryOptionLines()
                                                    : [];
                                                if (extraCostLines.length === 0 && mandatoryOptionLines.length === 0)
                                                    return null;
                                                return (
                                                    <div className="extra-order-costs-border">
                                                        {extraCostLines.map((cost, idx) => (
                                                            <div key={idx}>
                                                                <p>{cost.description ?? cost.name}</p>
                                                                <p>€ {parseFloat(cost.price).toFixed(2)}</p>
                                                            </div>
                                                        ))}
                                                        {mandatoryOptionLines.map((opt, idx) => (
                                                            <div key={`mo-${idx}`}>
                                                                <p>
                                                                    {textData?.["sections/label"]?.["mandatory"]}:{" "}
                                                                    {opt.name}
                                                                </p>
                                                                <p>€ {parseFloat(opt.price).toFixed(2)}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                            {(priceBreakdown?.minimum_order_surcharge ?? 0) > 0 && (
                                                <div className="total">
                                                    <p>Minimale orderkosten</p>
                                                    <p>
                                                        €{" "}
                                                        {parseFloat(
                                                            priceBreakdown?.minimum_order_surcharge ?? 0
                                                        ).toFixed(2)}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="total">
                                                <p>{textData["popups/order_overview"]["subtotal"]}</p>
                                                <p>€ {parseFloat(getDisplayPricing().subtotal).toFixed(2)}</p>
                                            </div>
                                            <div className="company_discount">
                                                {(priceBreakdown?.discount_codes_amount ?? 0) > 0 && (
                                                    <>
                                                        <div className="discount-container">
                                                            <p className="discount">
                                                                {props.textData["checkout/form"]["discount_code"]}{" "}
                                                            </p>
                                                            <p className="discount">
                                                                {"€ -" +
                                                                    parseFloat(
                                                                        priceBreakdown?.discount_codes_amount ?? 0
                                                                    ).toFixed(2)}
                                                            </p>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                            <div className="company_discount">
                                                {loggedInUser?.company?.price_settings !== null &&
                                                    loggedInUser?.company?.price_settings.discount > 0 &&
                                                    (priceBreakdown?.company_discount ?? 0) > 0 &&
                                                    !loggedInUser?.company?.price_settings.fixed_discount &&
                                                    props.order.status === "quotation" &&
                                                    (typeof props.order.discount === "undefined" ||
                                                        props.order.discount === null) && (
                                                        <>
                                                            <div className="discount-container">
                                                                <p className="discount">
                                                                    {
                                                                        props.textData["checkout/form"][
                                                                            "company_discount"
                                                                        ]
                                                                    }{" "}
                                                                    {"(" +
                                                                        parseFloat(
                                                                            loggedInUser?.company?.price_settings
                                                                                .discount
                                                                        ) +
                                                                        "%)"}
                                                                </p>
                                                                <p className="discount">
                                                                    {"€ -" +
                                                                        parseFloat(
                                                                            priceBreakdown?.company_discount ?? 0
                                                                        ).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}
                                                {loggedInUser?.company?.price_settings !== null &&
                                                    loggedInUser?.company?.price_settings.discount > 0 &&
                                                    (priceBreakdown?.company_discount ?? 0) > 0 &&
                                                    !!loggedInUser?.company?.price_settings.fixed_discount &&
                                                    props.order.status === "quotation" &&
                                                    (typeof props.order.discount === "undefined" ||
                                                        props.order.discount === null) && (
                                                        <>
                                                            <div className="discount-container">
                                                                <p className="discount">
                                                                    {
                                                                        props.textData["checkout/form"][
                                                                            "company_discount"
                                                                        ]
                                                                    }{" "}
                                                                    {"(€-" +
                                                                        parseFloat(
                                                                            loggedInUser?.company?.price_settings
                                                                                .discount
                                                                        ) +
                                                                        ")"}
                                                                </p>
                                                                <p className="discount">
                                                                    {"€ -" +
                                                                        parseFloat(
                                                                            priceBreakdown?.company_discount ?? 0
                                                                        ).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}
                                                {typeof props.order.discount !== "undefined" &&
                                                    props.order.discount !== null &&
                                                    !props.order.fixed_discount &&
                                                    (priceBreakdown?.company_discount ?? 0) > 0 && (
                                                        <>
                                                            <div className="discount-container">
                                                                <p className="discount">
                                                                    {
                                                                        props.textData["checkout/form"][
                                                                            "company_discount"
                                                                        ]
                                                                    }{" "}
                                                                    {"(" + parseFloat(props.order.discount) + "%)"}
                                                                </p>
                                                                <p className="discount">
                                                                    {"€ -" +
                                                                        parseFloat(
                                                                            priceBreakdown?.company_discount ?? 0
                                                                        ).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}
                                                {typeof props.order.discount !== "undefined" &&
                                                    props.order.discount !== null &&
                                                    !!props.order.fixed_discount &&
                                                    (priceBreakdown?.company_discount ?? 0) > 0 && (
                                                        <>
                                                            <div className="discount-container">
                                                                <p className="discount">
                                                                    {
                                                                        props.textData["checkout/form"][
                                                                            "company_discount"
                                                                        ]
                                                                    }{" "}
                                                                    {"(€-" + parseInt(props.order.discount) + ")"}
                                                                </p>
                                                                <p className="discount">
                                                                    {"€ -" +
                                                                        parseFloat(
                                                                            priceBreakdown?.company_discount ?? 0
                                                                        ).toFixed(2)}
                                                                </p>
                                                            </div>
                                                        </>
                                                    )}
                                            </div>
                                            <div className="shipping_cost">
                                                <p>{textData["popups/order_overview"]["shipping_costs"]}</p>
                                                <p>
                                                    €{" "}
                                                    {parseFloat(
                                                        priceBreakdown?.shipping ??
                                                            props.order.delivery_price ??
                                                            shippingCosts ??
                                                            0
                                                    ).toFixed(2)}
                                                </p>
                                            </div>
                                            {(priceBreakdown?.vat ?? 0) > 0 && showOrderBtw(props.order) && (
                                                <div className="tax">
                                                    <p>{textData["popups/order_overview"]["vat"]} (21%)</p>
                                                    <p>€ {parseFloat(getDisplayPricing().vat).toFixed(2)}</p>
                                                </div>
                                            )}
                                            <div className="total">
                                                <p>{textData["popups/order_overview"]["total"]}</p>
                                                <p>€ {parseFloat(getDisplayPricing().total).toFixed(2)}</p>
                                            </div>
                                        </div>
                                        <div className="delivery-overview">
                                            {props.order.status === "quotation" && currentLabel !== "2" && (
                                                <p>
                                                    {textData["popups/order_overview"]["shipping_info_quotation"]
                                                        .replace("{days}", props.order.shipping_days)
                                                        .replace("{date}", props.order.orderDate)}
                                                </p>
                                            )}
                                            {props.order.status !== "quotation" &&
                                                currentLabel !== "2" &&
                                                (props.order.delivery_term === null ||
                                                    props.order.delivery_term === "") && (
                                                    <p>
                                                        {textData["popups/order_overview"]["shipping_info_order"]
                                                            .replace("{delivery_date}", "to be determined")
                                                            .replace("{date}", props.order.orderDate)}
                                                    </p>
                                                )}
                                            {props.order.status !== "quotation" &&
                                                currentLabel !== "2" &&
                                                !(
                                                    props.order.delivery_term === null ||
                                                    props.order.delivery_term === ""
                                                ) && (
                                                    <p>
                                                        {textData["popups/order_overview"]["shipping_info_order"]
                                                            .replace("{delivery_date}", props.order.delivery_term)
                                                            .replace("{date}", props.order.orderDate)}
                                                    </p>
                                                )}

                                            {props.order.status === "quotation" && currentLabel === "2" && (
                                                <p>
                                                    This quotation was made on {props.order.orderDate}. Shipping details
                                                    will be determined later.
                                                </p>
                                            )}
                                            {props.order.status !== "quotation" &&
                                                currentLabel === "2" &&
                                                (props.order.delivery_term === null ||
                                                    props.order.delivery_term === "") && (
                                                    <p>
                                                        This order was made on {props.order.orderDate}. Shipping details
                                                        will be determined later.
                                                    </p>
                                                )}
                                        </div>
                                    </>
                                )}
                                {shippingCosts === "..." && props.order.status === "quotation" && (
                                    <>
                                        <div className="generic-spinner">
                                            <span className="spinner"></span>
                                        </div>
                                    </>
                                )}
                            </div>
                            {documents.length > 0 && (
                                <div className="documents-section">
                                    <h3>Documenten</h3>
                                    <div className="documents-list">
                                        {documents.map((doc, index) => (
                                            <a
                                                key={doc.id}
                                                href={`${doc.download_url}?user_id=${loggedInUser?.user?.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="document-item"
                                            >
                                                <span className="document-name">
                                                    {doc.description || `Attachment-${index + 1}`}
                                                </span>
                                                <span className="document-size">{(doc.size / 1024).toFixed(0)} KB</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="footer">
                                {showOnlyDownloadBtn && (
                                    <>
                                        {props.order.status !== "rejected" && (
                                            <button className="button-download-only" onClick={handleDownload}>
                                                {textData["popups/accept_quotation"]["buttons"]["download_as_pdf"]}
                                            </button>
                                        )}
                                        {props.order.status === "rejected" && (
                                            <button className="button-download-only disabled" disabled>
                                                {textData["popups/accept_quotation"]["buttons"]["download_as_pdf"]}
                                            </button>
                                        )}
                                    </>
                                )}
                                {(!showOnlyDownloadBtn || typeof showOnlyDownloadBtn === "undefined") && (
                                    <>
                                        {props.order.status !== "rejected" && (
                                            <button className="button-download" onClick={handleDownload}>
                                                {textData["popups/accept_quotation"]["buttons"]["download"]}
                                            </button>
                                        )}
                                        {(props.order.admin_edited !== true || impersonating) && (
                                            <button className="button-edit" onClick={handleEdit}>
                                                {textData["popups/accept_quotation"]["buttons"]["edit"]}
                                            </button>
                                        )}
                                        {!disableCheckoutButton && (
                                            <>
                                                <button className="button-agree" onClick={handleAgree}>
                                                    {textData["popups/accept_quotation"]["buttons"]["accept"]}
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </Popup>
            )}
        </>
    );
    return <OrderOverviewModal />;
}
